import axios from 'axios';
import store from '../Store';
import { clearAuthData } from '../redux/AuthSlice';
import { toast } from 'react-toastify';
import { api_sanctum, api_url } from './Config';

// Create axios instance with default config
const api = axios.create({
    withCredentials: true,  // Enable sending cookies with requests
    headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
    }
});

// Initialize CSRF token with retry limit
const initializeCSRF = async (retryCount = 0) => {
    const MAX_RETRIES = 3;
    try {
        await axios.get(`${api_sanctum}/sanctum/csrf-cookie`);
    } catch (error) {
        console.error('Failed to initialize CSRF token:', error);
        if (retryCount < MAX_RETRIES) {
            return initializeCSRF(retryCount + 1);
        }
    }
};

// Track if we're currently refreshing CSRF
let isRefreshingCSRF = false;
let refreshSubscribers = [];

// Helper to process queued requests
const processQueue = (error = null) => {
    refreshSubscribers.forEach(callback => {
        if (error) {
            callback(Promise.reject(error));
        } else {
            callback(Promise.resolve());
        }
    });
    refreshSubscribers = [];
};

// Get CSRF token from meta tag
const getCSRFToken = () => {
    return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
};

// Add request interceptor for CSRF token
api.interceptors.request.use(
    (config) => {
        const token = getCSRFToken();
        
        if (token) {
            config.headers['X-CSRF-TOKEN'] = token;
            config.headers['X-XSRF-TOKEN'] = token;
        }

        const authToken = localStorage.getItem('authToken');
        if (authToken) {
            config.headers['Authorization'] = `Bearer ${authToken}`;
        }

        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Response interceptor for handling authentication and subscription errors
api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;

        // Handle CSRF token mismatch
        if (error.response?.status === 419 && !originalRequest._retry) {
            originalRequest._retry = true;

            // If already refreshing CSRF, queue this request
            if (isRefreshingCSRF) {
                return new Promise(resolve => {
                    refreshSubscribers.push((token) => {
                        resolve(api(originalRequest));
                    });
                });
            }

            isRefreshingCSRF = true;

            try {
                await initializeCSRF();
                processQueue();
                isRefreshingCSRF = false;
                return api(originalRequest);
            } catch (refreshError) {
                processQueue(refreshError);
                isRefreshingCSRF = false;
                toast.error('Session expired. Please refresh the page.');
                return Promise.reject(refreshError);
            }
        }

        // Handle authentication error
        if (error.response?.data.message === "unAuthenticated") {
            store.dispatch(clearAuthData());
            toast.error('Session expired. Please login again');
            return Promise.reject(error);
        }

        // Handle subscription expiration
        if (error.response?.data.message === "subscription-expired") {
            store.dispatch(clearAuthData());
            toast.error('You do not have access to this tool. Kindly contact your administrator!');
            return Promise.reject(error);
        }

        // Handle other errors
        // if (error.response?.data.message) {
        //     toast.error(error.response.data.message);
        // } else {
        //     toast.error('An error occurred. Please try again.');
        // }

        return Promise.reject(error);
    }
);

// Initialize CSRF token when the file is imported
initializeCSRF();

export default api;